import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Box, Stack, Typography, IconButton } from '@mui/material';
// components
import Iconify from '../../components/Iconify';
// utils
import { fKrDeadlineDateHourKST } from '../../utils/formatTime';
// dialogs
import OfferDeadlineGuideDialog from './OfferDeadlineGuideDialog';

// ----------------------------------------------------------------------

OfferDeadlineInfo.propTypes = {
  priorityMatchDeadline: PropTypes.string,
  offerFinalDeadline: PropTypes.string,
};

export default function OfferDeadlineInfo({ priorityMatchDeadline, offerFinalDeadline }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOpenGuideDialog, setIsOpenGuideDialog] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fallback for legacy API without new deadline fields
  if (!priorityMatchDeadline && !offerFinalDeadline) {
    return (
      <Typography variant="body1" paragraph>
        <b>6시간 안에 결정</b> 해주세요. 6시간이 지나면 매칭이 취소됩니다.
      </Typography>
    );
  }

  const now = currentTime;
  const priorityDeadline = priorityMatchDeadline ? new Date(priorityMatchDeadline) : null;
  const finalDeadline = offerFinalDeadline ? new Date(offerFinalDeadline) : null;

  const isPriorityPeriod = priorityDeadline && now < priorityDeadline;
  const isWaitlistPeriod = !isPriorityPeriod && finalDeadline && now < finalDeadline;
  const isExpired = finalDeadline && now >= finalDeadline;

  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;

    const diff = deadline.getTime() - now.getTime();
    if (diff <= 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    }
    return `${minutes}분`;
  };

  const priorityTimeRemaining = isPriorityPeriod ? getTimeRemaining(priorityDeadline) : null;
  const finalTimeRemaining = isWaitlistPeriod ? getTimeRemaining(finalDeadline) : null;

  if (isExpired) {
    return null;
  }

  const InfoButton = () => (
    <IconButton
      size="small"
      onClick={() => setIsOpenGuideDialog(true)}
      sx={{ p: 0.25, ml: 0.5 }}
    >
      <Iconify icon="eva:info-outline" sx={{ width: 20, height: 20, color: 'text.disabled' }} />
    </IconButton>
  );

  // 우선 매칭 기간
  if (isPriorityPeriod && priorityTimeRemaining) {
    return (
      <Box sx={{ py: 1 }}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Iconify icon="eva:clock-outline" sx={{ width: 18, height: 18, color: 'text.secondary' }} />
            <Typography variant="body1">
              <b>{priorityTimeRemaining}</b> 안에 수락하면 우선 매칭돼요.
            </Typography>
            <InfoButton />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2.75 }}>
            {fKrDeadlineDateHourKST(offerFinalDeadline)}까지 수락할 수 있어요.
          </Typography>
        </Stack>
        <OfferDeadlineGuideDialog
          isOpen={isOpenGuideDialog}
          close={() => setIsOpenGuideDialog(false)}
          isPriorityPeriod={isPriorityPeriod}
          isWaitlistPeriod={isWaitlistPeriod}
        />
      </Box>
    );
  }

  // 대기열 기간
  if (isWaitlistPeriod && finalTimeRemaining) {
    return (
      <Box sx={{ py: 1 }}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems={{ xs: "start", sm: "center" }}>
            <Iconify icon="eva:alert-circle-outline" sx={{ width: 22, height: 22, color: 'warning.main' }} />
            <Typography variant="body1">
              <b>{fKrDeadlineDateHourKST(offerFinalDeadline)}</b>까지 수락할 수 있어요.
            </Typography>
            <InfoButton />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', pl: 3 }}>
            마감 후엔 응답할 수 없어요.
          </Typography>
        </Stack>
        <OfferDeadlineGuideDialog
          isOpen={isOpenGuideDialog}
          close={() => setIsOpenGuideDialog(false)}
          isPriorityPeriod={isPriorityPeriod}
          isWaitlistPeriod={isWaitlistPeriod}
        />
      </Box>
    );
  }

  return null;
}
